import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import '../styles/estate-plan-page.css';

const WillBasedEstatePlan: React.FC = () => {
  const [expandedDoc, setExpandedDoc] = useState<string | null>(null);
  const [expandedFaq, setExpandedFaq] = useState<string | null>(null);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [numPersons, setNumPersons] = useState<'1' | '2'>('1');
  const [subscribeLEP, setSubscribeLEP] = useState<'1' | '0'>('1');
  const navigate = useNavigate();
  const { addEstatePlan } = useCart();

  const toggleDoc = (id: string) => {
    setExpandedDoc(expandedDoc === id ? null : id);
  };

  const toggleFaq = (id: string) => {
    setExpandedFaq(expandedFaq === id ? null : id);
  };

  const getPrice = () => {
    return numPersons === '1' ? 199 : 299;
  };

  const handlePurchase = async () => {
    // Product 673 = Will-Based Estate Plan
    const formType = numPersons === '1' ? 'solo' : '2person';
    await addEstatePlan(673, formType, subscribeLEP === '1');
    navigate('/checkout');
  };

  return (
    <main className="estate-plan-page">
      {/* Hero Banner */}
      <section className="ep-hero">
        <div className="container">
          <div className="ep-hero-grid">
            <div className="ep-hero-content">
              <p className="ep-hero-subtitle"><em>Estate Planning in <strong>Louisiana</strong></em></p>
              <h1><em className="text-blue">Will-Based</em> <strong>Estate Plan</strong></h1>
              <p className="ep-hero-description">
                <em>Create a will-based plan to control your legacy.</em>
              </p>
              <div className="ep-hero-buttons pill-group">
                <button onClick={() => setShowPurchaseModal(true)} className="btn btn-pill-left">Start My Plan</button>
                <Link to="/estate-planning" className="btn btn-pill-right">Is this plan right for me?</Link>
              </div>
            </div>
            <div className="ep-hero-image">
              <img src="/wp-content/uploads/2022/02/Will-Based-Estate-Plan-1024x683.jpg" alt="Will-Based Estate Plan" />
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="ep-process">
        <div className="container">
          <h2 className="section-title"><strong>The </strong><em className="text-blue">simplest</em><strong> process</strong></h2>
          <p className="section-subtitle"><em>Three easy steps and everything is online</em></p>
          <div className="process-grid">
            <div className="process-step">
              <h3><em className="text-blue">#1</em> <strong>Answer a few easy questions</strong></h3>
              <p><em>Create your documents by answering questions that will allow you to tailor your documents to suit your specific needs.</em></p>
            </div>
            <div className="process-step">
              <h3><em className="text-blue">#2</em> <strong>Download or ship documents</strong></h3>
              <p><em>Download your documents from your private account dashboard, or request shipping at checkout.</em></p>
            </div>
            <div className="process-step">
              <h3><em className="text-blue">#3</em> <strong>Sign documents at your convenience</strong></h3>
              <p><em>Follow specific instructions to finalize your documents with a notary and two witnesses to make them legally binding.</em></p>
            </div>
          </div>
        </div>
      </section>

      {/* Build Plan Section */}
      <section className="ep-build-section">
        <div className="container">
          <div className="ep-build-grid">
            <div className="ep-build-card">
              <h2><strong><em>Build your</em></strong><br /><span className="text-blue"><em><strong>Will-Based Estate Plan</strong></em></span></h2>
              <button onClick={() => setShowPurchaseModal(true)} className="btn btn-solid btn-lg">Get Started</button>
              <p className="guarantee-text"><em><strong>Money-Back Guarantee!</strong> If you are unsatisfied with your completed documents, contact us within 30-days of your purchase to request a refund under our Refund Policy.</em></p>
            </div>
            <div className="ep-documents">
              <div className="documents-header">
                <h2><strong>Included </strong><em className="text-blue">documents</em></h2>
                <p><em>Simply download or order mailing</em></p>
              </div>
              <div className="documents-accordion">
                <div className={`doc-item ${expandedDoc === 'will' ? 'expanded' : ''}`}>
                  <button onClick={() => toggleDoc('will')}>
                    <span>Last Will &amp; Testament</span>
                    <span className="toggle-icon">{expandedDoc === 'will' ? '−' : '+'}</span>
                  </button>
                  <div className="doc-content">
                    <p>A Last Will and Testament is essentially a gift which takes effect at death. It can be changed at any time prior to death as long as the maker has capacity. A Will directs how debts of a person are to be paid at death, who will receive assets, who will control administration of an estate, and may appoint tutors (a/k/a guardian) of minor children. A Will may also include testamentary trusts that provide asset protection for a surviving spouse and children.</p>
                  </div>
                </div>
                <div className={`doc-item ${expandedDoc === 'financial-poa' ? 'expanded' : ''}`}>
                  <button onClick={() => toggleDoc('financial-poa')}>
                    <span>Financial Power of Attorney</span>
                    <span className="toggle-icon">{expandedDoc === 'financial-poa' ? '−' : '+'}</span>
                  </button>
                  <div className="doc-content">
                    <p>This document authorizes a person (an "agent") to make legal and financial decisions for another person (the "principal") during life.</p>
                  </div>
                </div>
                <div className={`doc-item ${expandedDoc === 'medical-poa' ? 'expanded' : ''}`}>
                  <button onClick={() => toggleDoc('medical-poa')}>
                    <span>Medical Power of Attorney</span>
                    <span className="toggle-icon">{expandedDoc === 'medical-poa' ? '−' : '+'}</span>
                  </button>
                  <div className="doc-content">
                    <p>This document authorizes a person (an "agent") to make medical decisions and access protected health information for another person (the "principal") if the person is unable to do so.</p>
                  </div>
                </div>
                <div className={`doc-item ${expandedDoc === 'living-will' ? 'expanded' : ''}`}>
                  <button onClick={() => toggleDoc('living-will')}>
                    <span>Advanced Healthcare Directive (Living Will)</span>
                    <span className="toggle-icon">{expandedDoc === 'living-will' ? '−' : '+'}</span>
                  </button>
                  <div className="doc-content">
                    <p>This document is a directive to healthcare providers regarding your preference for the withdrawal of artificial life-support in the event you are in a terminal and irreversible condition with no meaningful hope of recovery as certified by two physicians, one of which is your attending physician.</p>
                  </div>
                </div>
                <div className={`doc-item ${expandedDoc === 'hipaa' ? 'expanded' : ''}`}>
                  <button onClick={() => toggleDoc('hipaa')}>
                    <span>HIPAA Release</span>
                    <span className="toggle-icon">{expandedDoc === 'hipaa' ? '−' : '+'}</span>
                  </button>
                  <div className="doc-content">
                    <p>This is a document that authorizes another person to receive your protected health information, which otherwise would be sealed under HIPAA (Health Insurance Portability and Accountability Act of 1986).</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="ep-benefits">
        <div className="container">
          <div className="benefits-grid">
            <div className="benefits-content">
              <h2><strong>Premium </strong><em className="text-blue">benefits</em></h2>
              <p className="benefits-subtitle"><em>Practical, affordable, and complete solutions for you</em></p>
              <ul className="benefits-list">
                <li><i className="fas fa-check"></i> Decide who will control your affairs</li>
                <li><i className="fas fa-check"></i> Leave specific gifts of money, possessions, property, etc.</li>
                <li><i className="fas fa-check"></i> Decide when and how your assets will be distributed</li>
                <li><i className="fas fa-check"></i> Decide who will benefit from your estate</li>
                <li><i className="fas fa-check"></i> Exclude individuals from receiving assets</li>
                <li><i className="fas fa-check"></i> Designate a financial agent to make decisions about your property</li>
                <li><i className="fas fa-check"></i> Designate a medical agent to make healthcare decisions for you</li>
                <li><i className="fas fa-check"></i> Grant someone you trust access to critical medical records</li>
                <li><i className="fas fa-check"></i> Decide what happens in a medical emergency</li>
                <li><i className="fas fa-check"></i> Nominate Guardians or Tutors for minor children</li>
                <li><i className="fas fa-check"></i> Establish protective trusts for beneficiaries</li>
              </ul>
            </div>
            <div className="benefits-images">
              <img src="/wp-content/uploads/2022/02/Will-Based-Estate-Plan-1.jpg" alt="" />
              <img src="/wp-content/uploads/2022/02/Will-Based-Estate-Plan-2.jpg" alt="" />
            </div>
          </div>
        </div>
      </section>

      {/* FAQ and Contact Section */}
      <section className="ep-faq-contact">
        <div className="container">
          <div className="faq-contact-grid">
            <div className="contact-card">
              <h2>What if I have another question?</h2>
              <p><strong>Support team:</strong> M-F, 8am-5pm CST<br /><strong>Call us:</strong> +1 (855) 213-6300</p>
              <Link to="/contact" className="btn btn-secondary">Drop us a message</Link>
            </div>
            <div className="faq-content">
              <h2><strong>Common </strong><em className="text-blue">questions</em></h2>
              <p className="faq-subtitle"><em>Practical, affordable, and complete solutions for you</em></p>
              <div className="faq-accordion">
                <div className={`faq-item ${expandedFaq === 'handle' ? 'expanded' : ''}`}>
                  <button onClick={() => toggleFaq('handle')}>
                    <span>Can I really handle documents myself?</span>
                    <span className="toggle-icon">{expandedFaq === 'handle' ? '−' : '+'}</span>
                  </button>
                  <div className="faq-answer">
                    <p><strong><span className="text-blue">Yes</span>, but you need to understand:</strong> You answer the questions and submit responses when complete. Your documents will be prepared and delivered to you within three (3) business days. Your responses will not be reviewed by anyone.</p>
                  </div>
                </div>
                <div className={`faq-item ${expandedFaq === 'legal-edge' ? 'expanded' : ''}`}>
                  <button onClick={() => toggleFaq('legal-edge')}>
                    <span>What's included in the <em>Legal Edge plan for $9.99/month</em>?</span>
                    <span className="toggle-icon">{expandedFaq === 'legal-edge' ? '−' : '+'}</span>
                  </button>
                  <div className="faq-answer">
                    <p><strong><em className="text-blue">Forever Revisions</em></strong><br />Unlimited revisions to your GeauxPlan at any time and for any reason!</p>
                    <p><strong><em className="text-blue">Anytime Upgrade to an Advanced Estate Plan</em></strong><br />Upgrade your GeauxPlan to an Advanced Estate Plan at any time and for any reason with an affiliated estate planning law firm and receive a 100% credit of your original GeauxPlans Fee!</p>
                    <p><strong><em className="text-blue">Maintenance for Life</em></strong><br />Any estate plan needs to be maintained, or eventually it may not work. Legal Edge Plan entitles you to an annual meeting with an affiliated estate planning law firm to review your GeauxPlan to make sure it still works for you - so you are "all set"!</p>
                    <Link to="/legal-edge-plan" className="btn btn-outline-blue btn-sm">Learn more about Legal Edge</Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Purchase Modal */}
      {showPurchaseModal && (
        <div className="modal-overlay" onClick={() => setShowPurchaseModal(false)}>
          <div className="modal-content purchase-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowPurchaseModal(false)}>&times;</button>

            <div className="text-center mb-4">
              <h2 style={{ color: '#1a1acc' }}>Will-Based Estate Plan</h2>
              <p className="text-muted fst-italic">
                The Will-Based Estate Plan includes a Last Will and Testament, Financial Power of Attorney, Medical Power of Attorney, as well as an Advanced Healthcare Directive (a/k/a "Living Will").
              </p>
              <p className="mb-4">Average time to build a plan: <strong style={{ color: '#1a1acc' }}>15 minutes</strong></p>
            </div>

            <div className="mb-3">
              <label className="form-label"><strong>1.</strong> For how many people do you want to prepare documents?</label>
              <select
                className="form-select"
                value={numPersons}
                onChange={(e) => setNumPersons(e.target.value as '1' | '2')}
              >
                <option value="1">For one person</option>
                <option value="2">For two people</option>
              </select>
            </div>

            <div className="mb-3">
              <label className="form-label">
                <strong>2.</strong> Would you like to subscribe to the <a href="/legal-edge-plan" target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'underline' }}>Legal Edge Plan</a> for $9.99/month to be protected from any mistakes?
              </label>
              <select
                className="form-select"
                value={subscribeLEP}
                onChange={(e) => setSubscribeLEP(e.target.value as '1' | '0')}
              >
                <option value="1">Yes, sure!</option>
                <option value="0">No, thank you</option>
              </select>
            </div>

            <div className="mb-4">
              <strong>Final Price:</strong>{' '}
              <strong style={{ fontSize: '1.25rem' }}>
                ${getPrice()}{subscribeLEP === '1' ? ' + $9.99/mo' : ''}
              </strong>
            </div>

            <button
              onClick={handlePurchase}
              className="btn btn-solid btn-lg w-100"
            >
              Purchase
            </button>

            <p className="text-muted fst-italic mt-3 mb-0" style={{ fontSize: '0.85rem' }}>
              <strong>Build your plan:</strong> after the purchase, at your convenience, you&rsquo;ll answer a series of questions to prepare your documents.
            </p>
          </div>
        </div>
      )}
    </main>
  );
};

export default WillBasedEstatePlan;
